import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarDaysIcon, MapPinIcon, ClockIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import type { TvEvent, TvTournament } from '../hooks/useTvData';
import { format, parseISO, isToday, isFuture, isPast, differenceInDays } from 'date-fns';

interface Props {
  tournament: TvTournament | null;
  events: TvEvent[];
}

const EVENT_META: Record<string, { color: string; accent: string; bg: string; icon: string }> = {
  round:    { color: 'text-emerald-300', accent: 'border-emerald-500/60', bg: 'bg-emerald-900/30', icon: '⛳' },
  'pro-am': { color: 'text-sky-300',     accent: 'border-sky-500/60',     bg: 'bg-sky-900/30',     icon: '🤝' },
  practice: { color: 'text-slate-300',   accent: 'border-slate-500/40',   bg: 'bg-slate-800/30',   icon: '🏌️' },
  briefing: { color: 'text-amber-300',   accent: 'border-amber-500/50',   bg: 'bg-amber-900/30',   icon: '📋' },
  awards:   { color: 'text-orange-300',  accent: 'border-orange-500/50',  bg: 'bg-orange-900/30',  icon: '🏆' },
  ceremony: { color: 'text-orange-300',  accent: 'border-orange-500/50',  bg: 'bg-orange-900/30',  icon: '🎖️' },
  dinner:   { color: 'text-rose-300',    accent: 'border-rose-500/40',    bg: 'bg-rose-900/30',    icon: '🍽️' },
  default:  { color: 'text-white/70',    accent: 'border-white/20',       bg: 'bg-white/5',        icon: '📌' },
};

function getEventMeta(type: string) {
  const key = type.toLowerCase();
  for (const [k, v] of Object.entries(EVENT_META)) {
    if (key.includes(k)) return v;
  }
  return EVENT_META.default;
}

function parseEventDate(dateStr: string): Date | null {
  try { return parseISO(dateStr); } catch { return null; }
}

const ITEMS_PER_PAGE_DESKTOP = 6;
const ITEMS_PER_PAGE_TABLET = 4;
const ITEMS_PER_PAGE_MOBILE = 3;
const PAGE_INTERVAL = 8000;

export default function TvScheduleSlide({ tournament, events }: Props) {
  const now = new Date();
  const [page, setPage] = useState(0);
  const [screenSize, setScreenSize] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');

  useEffect(() => {
    function update() {
      const w = window.innerWidth;
      if (w < 640) setScreenSize('mobile');
      else if (w < 1024) setScreenSize('tablet');
      else setScreenSize('desktop');
    }
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const itemsPerPage = screenSize === 'mobile'
    ? ITEMS_PER_PAGE_MOBILE
    : screenSize === 'tablet'
    ? ITEMS_PER_PAGE_TABLET
    : ITEMS_PER_PAGE_DESKTOP;

  const sorted = [...events].sort((a, b) => {
    const da = parseEventDate(a.date);
    const db = parseEventDate(b.date);
    if (!da || !db) return 0;
    return da.getTime() - db.getTime();
  });

  const upcoming = sorted.filter((e) => {
    const d = parseEventDate(e.date);
    return d ? isToday(d) || isFuture(d) : true;
  });

  const displayEvents = upcoming.length > 0 ? upcoming : sorted;
  const totalPages = Math.max(1, Math.ceil(displayEvents.length / itemsPerPage));

  useEffect(() => {
    setPage(0);
  }, [events.length, itemsPerPage]);

  useEffect(() => {
    if (totalPages <= 1) return;
    const t = setInterval(() => setPage((p) => (p + 1) % totalPages), PAGE_INTERVAL);
    return () => clearInterval(t);
  }, [totalPages]);

  const pageEvents = displayEvents.slice(page * itemsPerPage, (page + 1) * itemsPerPage);

  const nextEvent = upcoming[0] ?? null;

  const tournamentStart = tournament?.start_date ? parseEventDate(tournament.start_date) : null;
  const tournamentEnd   = tournament?.end_date   ? parseEventDate(tournament.end_date)   : null;

  function getDaysBadge(dateStr: string): { label: string; urgent: boolean } | null {
    const d = parseEventDate(dateStr);
    if (!d) return null;
    if (isToday(d)) return { label: 'Today', urgent: true };
    if (isFuture(d)) {
      const diff = differenceInDays(d, now);
      if (diff === 1) return { label: 'Tomorrow', urgent: true };
      return { label: `In ${diff} days`, urgent: false };
    }
    if (isPast(d)) return { label: 'Completed', urgent: false };
    return null;
  }

  const gridCols = screenSize === 'mobile'
    ? 'grid-cols-1'
    : screenSize === 'tablet'
    ? 'grid-cols-2'
    : 'grid-cols-3';

  return (
    <div className="flex flex-col h-full bg-[#0a1f2e] text-white overflow-hidden">
      <div
        className="relative flex items-stretch justify-between px-4 sm:px-8 lg:px-14 py-0 border-b border-white/10 overflow-hidden flex-shrink-0"
        style={{ background: 'linear-gradient(135deg, #0c3050 0%, #0a2035 60%, #071828 100%)' }}
      >
        <div className="absolute inset-0 opacity-5"
          style={{ backgroundImage: 'repeating-linear-gradient(45deg, white 0, white 1px, transparent 0, transparent 50%)', backgroundSize: '24px 24px' }}
        />

        <div className="relative flex items-center gap-3 sm:gap-6 py-4 sm:py-7 min-w-0">
          <div className="w-10 h-10 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-golf-green flex items-center justify-center shadow-xl shadow-golf-green/20 flex-shrink-0">
            <CalendarDaysIcon className="w-6 h-6 sm:w-9 sm:h-9 text-white" />
          </div>
          <div className="min-w-0">
            <p className="text-golf-green text-xs sm:text-sm font-bold uppercase tracking-[0.2em]">Tournament Schedule</p>
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold font-display leading-tight mt-0.5 truncate">
              {tournament?.name ?? 'Events'}
            </h2>
            {tournamentStart && tournamentEnd && (
              <p className="text-white/40 text-xs sm:text-sm mt-1">
                {format(tournamentStart, 'MMM d')} – {format(tournamentEnd, 'MMM d, yyyy')}
                {tournament?.venue && <span className="hidden sm:inline ml-2">· {tournament.venue}</span>}
              </p>
            )}
          </div>
        </div>

        <div className="relative hidden sm:flex items-center gap-6 lg:gap-10 py-7 flex-shrink-0">
          <div className="text-right">
            <p className="text-white/35 text-xs uppercase tracking-widest">Today</p>
            <p className="text-white text-lg lg:text-2xl font-bold">{format(now, 'EEEE')}</p>
            <p className="text-white/60 text-sm lg:text-lg">{format(now, 'MMMM d, yyyy')}</p>
          </div>

          {nextEvent && (
            <div className="border-l border-white/10 pl-6 lg:pl-10">
              <p className="text-white/35 text-xs uppercase tracking-widest mb-1">Next Event</p>
              <p className="text-golf-orange font-bold text-base lg:text-lg leading-tight">{nextEvent.title}</p>
              <div className="flex items-center gap-2 lg:gap-3 mt-1 text-white/50 text-xs sm:text-sm">
                <span className="flex items-center gap-1">
                  <ClockIcon className="w-3 h-3 lg:w-3.5 lg:h-3.5" />
                  {nextEvent.time}
                </span>
                <span className="flex items-center gap-1">
                  <MapPinIcon className="w-3 h-3 lg:w-3.5 lg:h-3.5" />
                  {nextEvent.location}
                </span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 flex flex-col overflow-hidden px-3 sm:px-6 lg:px-10 py-3 sm:py-4 lg:py-6 gap-3 sm:gap-4">
        <AnimatePresence mode="wait">
          <motion.div
            key={`${page}-${screenSize}`}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.45, ease: 'easeInOut' }}
            className={`grid ${gridCols} gap-2 sm:gap-3 lg:gap-4 flex-1`}
          >
            {pageEvents.map((event, idx) => {
              const d = parseEventDate(event.date);
              const meta = getEventMeta(event.type);
              const isNow = d ? isToday(d) : false;
              const done = d ? isPast(d) && !isToday(d) : false;
              const badge = getDaysBadge(event.date);

              return (
                <motion.div
                  key={event.id}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.05, duration: 0.35 }}
                  className={`relative flex flex-col rounded-xl lg:rounded-2xl border overflow-hidden transition-all ${
                    isNow
                      ? 'border-golf-orange/60 shadow-lg shadow-golf-orange/10'
                      : done
                      ? 'border-white/8 opacity-50'
                      : `${meta.accent}`
                  } ${meta.bg}`}
                >
                  {isNow && (
                    <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-golf-orange via-yellow-400 to-golf-orange" />
                  )}

                  <div className="flex items-start justify-between p-3 sm:p-4 lg:p-5 pb-2 sm:pb-3">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center text-base sm:text-xl ${meta.bg} border ${meta.accent}`}>
                        {meta.icon}
                      </div>
                      <span className={`text-[10px] sm:text-xs font-bold uppercase tracking-widest ${meta.color}`}>
                        {event.type}
                      </span>
                    </div>
                    {badge && (
                      <span className={`text-[10px] sm:text-xs font-bold px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full flex-shrink-0 ${
                        badge.urgent
                          ? 'bg-golf-orange/20 text-golf-orange border border-golf-orange/40'
                          : done
                          ? 'bg-white/10 text-white/30 border border-white/10'
                          : 'bg-white/10 text-white/50 border border-white/15'
                      }`}>
                        {badge.label}
                        {badge.urgent && badge.label === 'Today' && (
                          <span className="inline-block w-1.5 h-1.5 rounded-full bg-golf-orange animate-pulse ml-1.5 align-middle" />
                        )}
                      </span>
                    )}
                  </div>

                  <div className="px-3 sm:px-5 pb-2 flex-1">
                    <h3 className={`text-base sm:text-lg lg:text-xl font-bold leading-tight mb-1 sm:mb-3 ${done ? 'text-white/40' : 'text-white'}`}>
                      {event.title}
                    </h3>
                    {event.description && (
                      <p className="text-white/40 text-xs sm:text-sm leading-relaxed line-clamp-2 mb-1 sm:mb-3 hidden sm:block">
                        {event.description}
                      </p>
                    )}
                  </div>

                  <div className="px-3 sm:px-5 pb-3 sm:pb-5 mt-auto">
                    <div className="flex flex-wrap gap-2 sm:gap-3 text-xs sm:text-sm">
                      <div className="flex items-center gap-1 sm:gap-1.5 text-white/55">
                        <CalendarDaysIcon className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span>{d ? format(d, 'EEE, MMM d') : event.date}</span>
                      </div>
                      <div className="flex items-center gap-1 sm:gap-1.5 text-white/55">
                        <ClockIcon className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span>{event.time}</span>
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-1 sm:gap-1.5 text-white/55">
                          <MapPinIcon className="w-3 h-3 sm:w-4 sm:h-4 flex-shrink-0" />
                          <span className="truncate">{event.location}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}

            {displayEvents.length === 0 && (
              <div className="col-span-full flex items-center justify-center h-full">
                <p className="text-white/25 text-xl sm:text-2xl">No events scheduled</p>
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {totalPages > 1 && (
          <div className="flex items-center justify-between flex-shrink-0 px-2">
            <p className="text-white/25 text-[10px] sm:text-xs uppercase tracking-widest">
              {pageEvents.length} of {displayEvents.length} events
            </p>
            <div className="flex items-center gap-2">
              {Array.from({ length: totalPages }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPage(i)}
                  className={`rounded-full transition-all duration-300 ${
                    i === page ? 'w-6 sm:w-8 h-2 bg-golf-green' : 'w-2 h-2 bg-white/20 hover:bg-white/40'
                  }`}
                />
              ))}
              <ChevronRightIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-white/20 ml-1" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
